/**
 * Money math. All amounts are integer minor units (the smallest indivisible
 * currency unit); no floats, ever (BLUEPRINT §4).
 */

/**
 * v1 winnings formula (draws.js):
 *   amount_won = bet + odds * bet
 * where `bet` is a major-unit amount and `odds` is a plain multiplier, then
 * stored into a DECIMAL(8,2) column (which rounds the whole expression to 2dp,
 * half away from zero).
 *
 * v2 restates this in integer units:
 *   betMinor       = bet in minor units
 *   oddsHundredths = odds * 100  (e.g. odds 30.00 -> 3000, odds 2 -> 200)
 *   winnings_minor = round_half_up( betMinor * (100 + oddsHundredths) / 100 )
 *
 * The single rounding of the full expression reproduces v1's DECIMAL rounding.
 * Inputs are assumed non-negative integers.
 */
export function computeWinningsMinor(betMinor: number, oddsHundredths: number): number {
  // betMinor * (100 + oddsHundredths) is the winnings expressed in hundredths
  // of a minor unit; divide by 100 and round half-up to whole minor units. All
  // integer.
  const hundredthsOfMinor = betMinor * (100 + oddsHundredths);
  return Math.floor((hundredthsOfMinor + 50) / 100);
}

/**
 * Describes how to render a currency amount: its ISO-ish code, the symbol
 * prefix, how many minor units make one major unit, and the separators between
 * whole-unit groups and the fractional part.
 */
export interface CurrencyFormat {
  code: string;
  symbol: string;
  minorUnitsPerMajor: number;
  thousandsSeparator: string;
  decimalSeparator: string;
}

/** PNG kina, matching v1's rendering (e.g. "K1,234.56"). */
export const DEFAULT_CURRENCY: CurrencyFormat = {
  code: 'PGK',
  symbol: 'K',
  minorUnitsPerMajor: 100,
  thousandsSeparator: ',',
  decimalSeparator: '.',
};

/**
 * Insert `separator` every 3 digits from the right, e.g. ("1234567", ",") ->
 * "1,234,567". A plain left-to-right scan — no backtracking regex.
 */
function groupThousands(digits: string, separator: string): string {
  let grouped = '';
  for (let i = 0; i < digits.length; i++) {
    const digitsLeft = digits.length - i;
    if (i > 0 && digitsLeft % 3 === 0) grouped += separator;
    grouped += digits[i];
  }
  return grouped;
}

/**
 * Format an integer minor-unit amount as a currency string per `fmt`,
 * e.g. with the default format: 3100 -> "K31.00", 123456 -> "K1,234.56",
 * -100 -> "-K1.00". A currency whose `minorUnitsPerMajor` is 1 has no
 * fractional part and renders without a decimal separator.
 */
export function formatCurrency(minor: number, fmt: CurrencyFormat = DEFAULT_CURRENCY): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(minor));
  const per = fmt.minorUnitsPerMajor;
  const whole = Math.floor(abs / per);
  const grouped = groupThousands(String(whole), fmt.thousandsSeparator);
  if (per <= 1) {
    return `${sign}${fmt.symbol}${grouped}`;
  }
  const remainder = abs % per;
  const fractionWidth = String(per - 1).length;
  const fraction = String(remainder).padStart(fractionWidth, '0');
  return `${sign}${fmt.symbol}${grouped}${fmt.decimalSeparator}${fraction}`;
}

/**
 * Format an integer minor-unit amount as a major-unit currency string, e.g.
 * with the default currency: 3100 -> "K31.00", 123456 -> "K1,234.56",
 * -100 -> "-K1.00".
 */
export function formatMajor(minor: number): string {
  return formatCurrency(minor);
}
